import React, { useState, useEffect, useRef } from "react";
import placeholder from "../assets/images/house-placeholder.webp";
import { Link, useLocation } from "react-router-dom";
import imgs from "./temp.json";
import constants from "../constants/constants";
import BouncingDotsLoader from "./loader.component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faAngleRight,
  faMagnifyingGlass,
  faX,
} from "@fortawesome/free-solid-svg-icons";
import SEO, { pageSEO } from "../components/seo.component";
import isReactSnap from "../utils/isReactSnap";

export default function Listings() {
  const { state } = useLocation();
  // Refresh once when the page is first opened in this browser session.
  // We use sessionStorage to avoid an infinite reload loop.
  useEffect(() => {
    try {
      const flagKey = "listings_has_reloaded_v1";
      if (typeof window !== "undefined" && !sessionStorage.getItem(flagKey)) {
        // mark as reloaded for this session, then reload shortly after mount
        sessionStorage.setItem(flagKey, "1");
        // small timeout so initial render can finish before reload
        setTimeout(() => {
          // reload the page once
          window.location.reload();
        }, 100);
      }
    } catch (err) {
      console.error("Listings reload error:", err);
    }
  }, []);
  useEffect(() => {
    console.log(constants.ENVIRONMENT);
  }, []);
  const [request, setRequest] = useState(
    `${constants.ENVIRONMENT === "development"
      ? `https://query.ampre.ca/odata/Property?$filter=(StandardStatus eq \'Active\')&$orderby=OriginalEntryTimestamp desc,ListingKey&$top=500`
      : `https://${constants.DOMAIN}/api/odata/Property?$filter=(StandardStatus eq \'Active\')&$orderby=OriginalEntryTimestamp desc,ListingKey&$top=500`
    }`
  );
  const [listings, setListings] = useState([]);
  const [media, setMedia] = useState(imgs.thumbs.value);
  const [formatted, setFormatted] = useState({});
  const [loaded, setLoaded] = useState(false);
  const loadedRef = useRef(false);
  const listingsRef = useRef([]);
  const [searching, setSearching] = useState(state ? 2 : 0);
  const [currentPage, setCurrentPage] = useState(1);
  function getData(url) {
    // Helper to convert a full upstream URL to proxy.php encoded path
    const toProxyPath = (fullUrl) => {
      try {
        // Extract path starting at 'odata/'
        const idx = fullUrl.indexOf("odata/");
        const path = idx >= 0 ? fullUrl.substring(idx) : fullUrl;
        // Replace markers for PHP proxy
        const encoded = path
          .replaceAll("?", "(Q)")
          .replaceAll(" ", "(S)")
          .replaceAll("&", "(A)");
        return encodeURIComponent(encoded);
      } catch (e) {
        return encodeURIComponent(fullUrl);
      }
    };
    var body = {
      url: url,
      needAuth: true,
      bearerToken: constants.TOKEN,
    };
    return new Promise(async (resolve, reject) => {
      try {
        // In local dev, use metaGet (node). In prod, use same-origin PHP proxy to avoid CORS.
        const isLocal =
          typeof window !== "undefined" &&
          (window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1");
        let data;
        if (isLocal) {
          data = await fetch(`http://${constants.URL}/metaGet`, {
            method: "POST",
            body: JSON.stringify(body),
            headers: { "Content-Type": "application/json" },
          });
        } else {
          const proxyPath = toProxyPath(url);
          const proxyUrl = `https://${constants.DOMAIN}/proxy.php?path=${proxyPath}`;
          data = await fetch(proxyUrl, { method: "GET" });
        }
        if (data.ok) {
          let res = await data.json();
          resolve(res);
        } else {
          reject("Failed to fetch data");
        }
      } catch (err) {
        reject(err);
      }
    });
  }

  useEffect(() => {
    if (isReactSnap()) return; // Skip fetching during react-snap
    if (loadedRef.current) {
      loadedRef.current = false;
      getMedia(
        listings?.slice(
          maxListings * (currentPage - 1),
          maxListings * (currentPage - 1) + maxListings
        )
      )
        .then((d) => {
          setMedia(d.value);
          setFormatted(formatMedia(d.value));
        })
        .catch((err) => {
        });
    }
  }, [currentPage]);

  function capitalize(val) {
    return String(val).charAt(0).toUpperCase() + String(val).slice(1);
  }

  function getMedia(listings) {
    // Build upstream Media query against query.ampre.ca and proxy via metaGet (prod & dev)
    let temp = `https://query.ampre.ca/odata/Media?$filter=ResourceRecordKey in (`;
    for (let l of listings) {
      temp = temp + `'${l.ListingKey}',`;
    }
    if (listings.length) {
      temp = temp.slice(0, -1);
    }
    temp =
      temp +
      ") and ImageSizeDescription eq 'Thumbnail' and PreferredPhotoYN eq true";

    const body = {
      url: temp,
      needAuth: true,
      bearerToken: constants.TOKEN,
    };

    return new Promise(async (resolve, reject) => {
      try {
        // Determine environment based on runtime hostname
        const isLocal =
          typeof window !== "undefined" &&
          (window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1");
        let data;
        if (isLocal) {
          const metaUrl = `http://${constants.URL}/metaGet`;
          data = await fetch(metaUrl, {
            method: "POST",
            body: JSON.stringify(body),
            headers: { "Content-Type": "application/json" },
          });
        } else {
          // Use same-origin PHP proxy in production
          const idx = temp.indexOf("odata/");
          const path = idx >= 0 ? temp.substring(idx) : temp;
          const encoded = encodeURIComponent(
            path.replaceAll("?", "(Q)").replaceAll(" ", "(S)").replaceAll("&", "(A)")
          );
          const proxyUrl = `https://${constants.DOMAIN}/proxy.php?path=${encoded}`;
          data = await fetch(proxyUrl, { method: "GET" });
        }

        if (data.ok) {
          const res = await data.json();
          resolve(res);
        } else {
          reject("Failed to fetch data");
        }
      } catch (err) {
        reject(err);
      }
    });
  }

  function formatMedia(media) {
    var temp = {};

    for (let img of media) {
      if (temp[img.ResourceRecordKey]) {
        temp[img.ResourceRecordKey] = [...temp[img.ResourceRecordKey], img];
      } else {
        temp[img.ResourceRecordKey] = [img];
      }
    }
    setLoaded(true);

    return temp;
  }

  useEffect(() => {
    if (isReactSnap()) return; // Skip fetching during react-snap
    if (!loadedRef.current) {
      loadedRef.current = true;
      getData(request)
        .then((data) => {
          setListings(data.value);
          listingsRef.current = data.value;

          if (data.value?.length) {
            getMedia(
              data.value?.slice(
                maxListings * (currentPage - 1),
                maxListings * (currentPage - 1) + maxListings
              )
            )
              .then((d) => {
                setMedia(d.value);
                setFormatted(formatMedia(d.value));
                setLoaded(true);
              })
              .catch((err) => {
                console.log("error in getMedia", err);
              });
          } else {
            setLoaded(true);
          }
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [request]);

  const [filter, setFilter] = useState("All");

  const [searchParams, setSearchParams] = useState({
    bedrooms: "",
    bathrooms: "",
    minPrice: "",
    maxPrice: "",
    cityArea: "", // Add new city/area filter
    address: "", // Add new address filter
  });

  /**
   * 
   * @param {*} type 
   * @returns 
   * ' (single quote) → %27 
   * Space → %20 (or sometimes + in query strings)
   * ? → %3F
   * & → %26
   */
  function getUrlBasedOnType(type) {
    if (type === "Agent") {
      return `(ListAgentKey eq \'${constants.AGENT_ID}\')`;
    } else if (type === "Office") {
      return `(ListOfficeKey eq \'${constants.OFFICE_ID}\')`;
    } else {
      return `(ListAgentKey eq \'${constants.AGENT_ID}\' or CoListAgentKey eq \'${constants.AGENT_ID}\' or ListOfficeKey eq \'${constants.OFFICE_ID}\')`;
    }
  }


  function updateSearch(key, val) {
    var temp = { ...searchParams };
    temp[key] = val;
    setSearchParams(temp);
  }

  // Function to capitalize first letter of each word
  function capitalizeFirstLetters(str) {
    if (!str || typeof str !== 'string') return str;
    return str.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  function runSearch(type) {
    if (!type) {
      type = filter;
    }

    if (constants.ENVIRONMENT === "development") {
      var str = `https://query.ampre.ca/odata/Property?$filter=(${getUrlBasedOnType(type)} and StandardStatus eq 'Active' `;
      if (!(type === "Agent" || type === "Office")) {
        str = "https://query.ampre.ca/odata/Property?$filter=(StandardStatus eq 'Active' ";
      }
    } else {
      var str = `https://${constants.DOMAIN}/api/odata/Property?$filter=(${getUrlBasedOnType(type)} and StandardStatus eq \'Active\' `;
      if (!(type === "Agent" || type === "Office")) {
        str = `https://${constants.DOMAIN}/api/odata/Property?$filter=(StandardStatus eq \'Active\' `;
      }
    }
    if (type !== "Reset") {
      let hasSearchParams = false;
      let anyValueNonEmpty = false;
      let toAdd = "";
      for (let [key, val] of Object.entries(searchParams)) {
        console.log(val);
        if (val && val !== "undefined" && val !== "null" && val.trim() !== "") {
          hasSearchParams = true;
          anyValueNonEmpty = true;
          toAdd = toAdd + val + " and ";
        }
      }
      console.log(hasSearchParams, anyValueNonEmpty);
      console.log(toAdd);
      // Remove trailing " and " if searchParams were added
      if (hasSearchParams) {
        str = str + "and ";
        str = str + toAdd;
        str = str.slice(0, -5); // Remove " and " (5 characters)
      }
    }
    str = str + ")";

    // if (
    //   type !== "Reset" &&
    //   (Object.entries(searchParams).filter(([k, v], i) => !!v)?.length ||
    //     type === "Agent" ||
    //     (filter === "Agent" && !type))
    // ) {
    //   str = str.slice(0, -5);
    //   str = str + "&$top=10000";
    // } else {
    //   str = str.slice(0, -9);
    //   str = str + "?$orderby=OriginalEntryTimestamp desc,ListingKey&$top=500";
    // }

    getData(str)
      .then((data) => {
        setListings(data.value);
        listingsRef.current = data.value;
        getMedia(
          data.value?.slice(
            maxListings * (currentPage - 1),
            maxListings * (currentPage - 1) + maxListings
          )
        )
          .then((d) => {
            console.log(d.value);
            setMedia(d.value);
            setFormatted(formatMedia(d.value));
            setSearching(
              (type &&
                !Object.entries(searchParams).filter(([k, v], i) => !!v)
                  ?.length) ||
                type === "Reset"
                ? 3
                : 2
            );

            setLoaded(true);
          })
          .catch((err) => {
            console.log(err);
          });
      })
      .catch((err) => {
        console.log(err);
      });
  }

  function formatStr(str) {
    // More careful replacement to preserve OData query structure
    const result = str
      .replaceAll(" and ", "(S)and(S)")
      .replaceAll(" or ", "(S)or(S)")
      .replaceAll(" eq ", "(S)eq(S)")
      .replaceAll(" ge ", "(S)ge(S)")
      .replaceAll(" le ", "(S)le(S)")
      .replaceAll(" lt ", "(S)lt(S)")
      .replaceAll(" gt ", "(S)gt(S)")
      .replaceAll("contains(", "contains(")
      .replaceAll("', '", "',(S)'")
      .replaceAll("')", "')")
      .replaceAll("(", "(")
      .replaceAll(")", ")")
      .replaceAll("&", "(A)")
      .replaceAll("?", "(Q)")
      .replaceAll(" ", "(S)");

    return result;
  }

  function numberRange(end) {
    return new Array(end).fill().map((d, i) => i + 1);
  }

  const maxPages = 5;
  const maxListings = 20;
  function getStart() {
    // get starting index of pagination numbers such that we display the same number of pages even if
    // current page is at start or end and current page is always in middle of slice
    return Math.max(
      0,
      Math.min(
        currentPage - Math.ceil(maxPages / 2),
        Math.ceil(listings?.length / maxListings) - maxPages
      )
    );
  }

  const seoConfig = pageSEO.listings();
  return (
    <>
      <SEO {...seoConfig} />
      <div className="max-w-[1100px] mx-auto px-7 mb-10 pt-[100px] min-h-[35vh]">
        {!searching || searching === 3 || searching === 4 ? (
          <>
            <div className=" w-full bg-slate-100 rounded-md py-4 px-8 mt-10">
              <h1 className="text-2xl text-black libre my-4 uppercase">
                Search
              </h1>
              <div className="flex flex-wrap gap-x-10 ">
                <div className="form-item max-w-40">
                  <label className="form-label ">Bedrooms</label>
                  <select
                    className="form-input "
                    onChange={(e) => {
                      if (e.target.value && e.target.value !== "undefined" && e.target.value !== "null" && e.target.value !== "") {
                        updateSearch("bedrooms", e.target.value);
                      } else {
                        updateSearch("bedrooms", "");
                      }
                    }}
                  >
                    <option value="">Select</option>
                    <option value="BedroomsTotal eq 1">1</option>
                    <option value="BedroomsTotal eq 2">2</option>
                    <option value="BedroomsTotal eq 3">3</option>
                    <option value="BedroomsTotal ge 3">3+</option>
                  </select>
                </div>
                <div className="form-item max-w-40">
                  <label className="form-label">Bathrooms</label>
                  <select
                    className="form-input "
                    onChange={(e) => {
                      if (e.target.value && e.target.value !== "undefined" && e.target.value !== "null" && e.target.value !== "") {
                        updateSearch("bathrooms", e.target.value);
                      } else {
                        updateSearch("bathrooms", "");
                      }
                    }}
                  >
                    <option value="">Select</option>
                    <option value="BathroomsTotalInteger eq 1">1</option>
                    <option value="BathroomsTotalInteger eq 2">2</option>
                    <option value="BathroomsTotalInteger eq 3">3</option>
                    <option value="BathroomsTotalInteger ge 3">3+</option>
                  </select>
                </div>
                <div className="form-item max-w-40">
                  <label className="form-label">City/Community</label>
                  <input
                    className="form-input max-h-[31px]"
                    placeholder="Enter city or community"
                    onChange={(e) => {
                      const value = e.target.value.trim();
                      if (value && value !== "undefined" && value !== "null" && value !== "") {
                        const capitalizedValue = capitalizeFirstLetters(value);
                        updateSearch(
                          "cityArea",
                          `contains(City, '${capitalizedValue}') or contains(CityRegion, '${capitalizedValue}') or contains(UnparsedAddress, '${capitalizedValue}')`
                        );
                      } else {
                        updateSearch("cityArea", "");
                      }
                    }}
                  />
                </div>
                <div className="form-item max-w-40">
                  <label className="form-label">Address</label>
                  <input
                    className="form-input max-h-[31px]"
                    placeholder="Enter street address"
                    onChange={(e) => {
                      const value = e.target.value.trim();
                      if (value && value !== "undefined" && value !== "null" && value !== "") {
                        const capitalizedValue = capitalizeFirstLetters(value);
                        updateSearch(
                          "address",
                          `contains(UnparsedAddress, '${capitalizedValue}') or contains(StreetName, '${capitalizedValue}') or contains(StreetNumber, '${capitalizedValue}')`
                        );
                      } else {
                        updateSearch("address", "");
                      }
                    }}
                  />
                </div>
                <div className="form-item max-w-40">
                  <label className="form-label">Minimum Price</label>
                  <input
                    className="form-input max-h-[31px]"
                    placeholder="Min"
                    onChange={(e) =>
                      updateSearch(
                        "minPrice",
                        `ListPrice ge ${e.target.value.replace(/\D/g, "")}`
                      )
                    }
                  ></input>
                </div>
                <div className="form-item max-w-40">
                  <label className="form-label">Maximum Price</label>
                  <input
                    className="form-input max-h-[31px]"
                    placeholder="Max"
                    onChange={(e) => {
                      if (e.target.value && e.target.value !== "undefined" && e.target.value !== "null" && e.target.value !== "") {
                        updateSearch(
                          "maxPrice",
                          `ListPrice le ${e.target.value.replace(/\D/g, "")}`
                        )
                      } else {
                        updateSearch("maxPrice", "");
                      }
                    }}
                  ></input>
                </div>
              </div>
              <button
                onClick={() => {
                  if (
                    Object.entries(searchParams).filter(([k, v], i) => !!v)
                      ?.length
                  ) {
                    runSearch();
                    setSearching(1);
                  }
                }}
                className="btn-rounded my-3 inline-block"
              >
                SEARCH
              </button>
            </div>
            <div className="flex justify-between items-end gap-x-10 flex-wrap">
              <h1
                className="text-4xl text-black py-8 libre mt-6"
              >
                Recent Listings
              </h1>
              <div className="flex-auto max-w-40 mb-8">
                <label className="form-label">Show:</label>
                {/* <select className="form-input" onChange={(e) => { setLoaded(false); setFilter(e.target.value); setRequest(e.target.value === 'Agent' ? `${constants.ENVIRONMENT === 'local' ? 'https://query.ampre.ca/' : `https://${constants.DOMAIN}/api/`}odata/Property?$filter=ListAgentKey eq \'${constants.AGENT_ID}\' or CoListAgentKey eq \'${constants.AGENT_ID}\' or ListOfficeKey eq \'${constants.OFFICE_ID}\'&orderby=OriginalEntryTimestamp desc,ListingKey&$top=1000` : `${constants.ENVIRONMENT === 'local' ? 'https://query.ampre.ca/' : `https://${constants.DOMAIN}/api/`}odata/Property?$orderby=OriginalEntryTimestamp desc,ListingKey&$top=500`) }} > */}
                <select
                  className="form-input"
                  value={filter}
                  onChange={(e) => {
                    setFilter(e.target.value);
                    setSearching(4);
                    runSearch(e.target.value);
                  }}
                >
                  <option value={"All"}>All Listings</option>
                  <option value={"Office"}>Office Listings</option>
                  <option value={"Agent"}>Agent Listings</option>
                  {/* <option value={`${constants.ENVIRONMENT === 'local' ? 'https://query.ampre.ca/' : `https://${constants.DOMAIN}/api/`}odata/Property?$top=1000`}>My Listings</option> */}
                </select>
              </div>
            </div>
          </>
        ) : null}

        {/* <div className={`grid ${listings?.length > 3 ? 'listing-grid' : 'listing-grid-min'} gap-9 min-h-[20vh] `}>
                            {listings.map((prop) =>
                                <Link className="w-full h-full " to={`/listing/${prop.ListingKey}`}>
                                    <div className="relative w-full aspect-[5/3] sm:aspect-[4/3] overflow-hidden">
                                        <img className="w-full h-full object-cover" src={formatted[prop.ListingKey]?.find((img) => img.Order === 0)?.MediaURL ?? formatted[prop.ListingKey]?.[0]?.MediaURL ?? placeholder} />
                                        <div className="px-2 py-1 bg-logo-second absolute bottom-0 w-full">
                                            <span className=" text-white text-sm font-semibold">{prop.CrossStreet}</span>
                                        </div>
                                    </div>
                           
                                </Link>

                            )}

                        </div>
                    } */}

        {searching === 1 || searching === 2 ? (
          <>
            <div className="flex flex-wrap gap-x-10 bg-slate-100 rounded-md pt-3 px-8 mt-10">
              <div className="form-item max-w-40">
                <label className="form-label ">Bedrooms</label>
                <select
                  className="form-input min-w-[144px]"
                  value={searchParams.bedrooms}
                  onChange={(e) => {
                    if (e.target.value && e.target.value !== "undefined" && e.target.value !== "null" && e.target.value !== "") {
                      updateSearch("bedrooms", e.target.value);
                    } else {
                      updateSearch("bedrooms", "");
                    }
                  }}
                >
                  <option value="">Select</option>
                  <option value="BedroomsTotal eq 1">1</option>
                  <option value="BedroomsTotal eq 2">2</option>
                  <option value="BedroomsTotal eq 3">3</option>
                  <option value="BedroomsTotal ge 3">3+</option>
                </select>
              </div>
              <div className="form-item max-w-40">
                <label className="form-label">Bathrooms</label>
                <select
                  className="form-input min-w-[144px]"
                  value={searchParams.bathrooms}
                  onChange={(e) => {
                    if (e.target.value && e.target.value !== "undefined" && e.target.value !== "null" && e.target.value !== "") {
                      updateSearch("bathrooms", e.target.value);
                    } else {
                      updateSearch("bathrooms", "");
                    }
                  }}
                >
                  <option value="">Select</option>
                  <option value="BathroomsTotalInteger eq 1">1</option>
                  <option value="BathroomsTotalInteger eq 2">2</option>
                  <option value="BathroomsTotalInteger eq 3">3</option>
                  <option value="BathroomsTotalInteger ge 3">3+</option>
                </select>
              </div>
              <div className="form-item max-w-40">
                <label className="form-label">City/Community</label>
                <input
                  className="form-input max-h-[31px]"
                  placeholder="Enter city or community"
                  defaultValue={
                    searchParams.cityArea
                      ? searchParams.cityArea.match(/'([^']+)'/)?.[1] || ""
                      : ""
                  }
                  onChange={(e) => {
                    const value = e.target.value.trim();
                    if (value && value !== "undefined" && value !== "null" && value !== "") {
                      const capitalizedValue = capitalizeFirstLetters(value);
                      updateSearch(
                        "cityArea",
                        `contains(City, '${capitalizedValue}') or contains(CityRegion, '${capitalizedValue}') or contains(UnparsedAddress, '${capitalizedValue}')`
                      );
                    } else {
                      updateSearch("cityArea", "");
                    }
                  }}
                />
              </div>
              <div className="form-item max-w-40">
                <label className="form-label">Min. Price</label>
                <input
                  className="form-input max-h-[31px]"
                  placeholder="Min"
                  defaultValue={searchParams.minPrice?.replace(/\D/g, "")}
                  onChange={(e) =>  
                    {
                      if (e.target.value && e.target.value !== "undefined" && e.target.value !== "null" && e.target.value !== "") {
                        updateSearch(
                          "minPrice",
                          `ListPrice ge ${e.target.value.replace(/\D/g, "")}`
                        )
                      } else {
                        updateSearch("minPrice", "");
                      }
                    }
                  }
                ></input>
              </div>
              <div className="form-item max-w-40">
                <label className="form-label">Max. Price</label>
                <input
                  className="form-input max-h-[31px]"
                  placeholder="Max"
                  defaultValue={searchParams.maxPrice?.replace(/\D/g, "")}
                  onChange={(e) =>
                    {
                      if (e.target.value && e.target.value !== "undefined" && e.target.value !== "null" && e.target.value !== "") {
                        updateSearch(
                          "maxPrice",
                          `ListPrice le ${e.target.value.replace(/\D/g, "")}`
                        )
                      } else {
                        updateSearch("maxPrice", "");
                      }
                    }
                  }
                ></input>
              </div>
              <button
                onClick={() => {
                  runSearch();
                  setSearching(1);
                }}
                className="btn-rounded my-3 inline-block self-center justify-self-end"
              >
                SEARCH
              </button>
              <button
                className="clear-btn self-center"
                onClick={() => {
                  setSearching(4);
                  setSearchParams({
                    bedrooms: "",
                    bathrooms: "",
                    minPrice: "",
                    maxPrice: "",
                    cityArea: "",
                  });
                  runSearch("Reset");
                }}
              >
                <FontAwesomeIcon className="fa-sm mr-2" icon={faX} />
                Clear
              </button>
            </div>
            <div className="flex justify-between items-end gap-x-10 flex-wrap">
              <h1 className="text-4xl text-black py-8 libre mt-6">{`Results ${state ? `For '${state}'` : ""
                }`}</h1>
              <div className="flex-auto max-w-40 mb-8">
                <label className="form-label">Show:</label>
                <select
                  value={filter}
                  className="form-input"
                  onChange={(e) => {
                    setSearching(1);
                    runSearch(e.target.value);
                    setFilter(e.target.value);
                  }}
                >
                  <option value={"All"}>All Listings</option>
                  <option value={"Agent"}>Office Listings</option>
                  {/* <option value={`${constants.ENVIRONMENT === 'local' ? 'https://query.ampre.ca/' : `https://${constants.DOMAIN}/api/`}odata/Property?$top=1000`}>My Listings</option> */}
                </select>
              </div>
            </div>{" "}
          </>
        ) : null}

        {!loaded || searching === 1 || searching == 4 ? (
          <div className="mt-32 min-h-[20vh]">
            <BouncingDotsLoader />
          </div>
        ) : null}
        {!listings?.length && loaded ? (
          <h3 className="font-semibold text-align-center mt-12">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="mr-2" /> No
            properties to show
          </h3>
        ) : null}

        {searching === 2 || searching === 3 || (searching === 0 && loaded) ? (
          <>
            <div className="grid listing-grid gap-9 min-h-[35vh] ">
              {listings
                ?.slice(
                  maxListings * (currentPage - 1),
                  maxListings * (currentPage - 1) + maxListings
                )
                ?.map((prop) => (
                  <Link
                    className="w-full h-full"
                    to={`/listing/${prop.ListingKey}`}
                  >
                    <div className="relative w-full aspect-[5/3] sm:aspect-[4/3] overflow-hidden">
                      <img
                        className="w-full h-full object-cover"
                        src={
                          formatted[prop.ListingKey]?.find(
                            (img) => img.Order === 0
                          )?.MediaURL ??
                          formatted[prop.ListingKey]?.[0]?.MediaURL ??
                          placeholder
                        }
                      />
                      <div className="px-2 py-1 bg-logo-second absolute bottom-0 w-full max-h-[56px]">
                        <span className=" text-white text-sm libre font-semibold">
                          {prop.CityRegion ?? prop.CrossStreet}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
            </div>
          </>
        ) : null}

        {listings?.length > maxListings &&
          (searching === 2 ||
            ((searching === 0 || searching === 3) && loaded)) ? (
          <>
            <div
              style={{ gridTemplateColumns: "1fr auto 1fr" }}
              className="mx-auto w-fit mt-8 grid grid-cols-[1fr auto 1fr] items-center gap-x-3"
            >
              {currentPage > 1 ? (
                <FontAwesomeIcon
                  icon={faAngleLeft}
                  className="cursor-pointer text-slate-400 fa-lg"
                  onClick={() => setCurrentPage(currentPage - 1)}
                />
              ) : (
                <div />
              )}
              <div className="flex gap-2">
                {numberRange(Math.ceil(listings?.length / maxListings))
                  .slice(getStart(), getStart() + maxPages)
                  .map((num) => (
                    <div
                      key={num}
                      onClick={() => setCurrentPage(num)}
                      className={`rounded-full w-9 h-9 flex items-center justify-center cursor-pointer text-center ${num === currentPage
                        ? "text-white bg-slate-400"
                        : "text-slate-400"
                        }`}
                    >
                      <span className={`font-bold text-lg `}>{num}</span>
                    </div>
                  ))}
              </div>
              {currentPage !== Math.ceil(listings?.length / maxListings) ? (
                <FontAwesomeIcon
                  icon={faAngleRight}
                  className="cursor-pointer text-slate-400 fa-lg"
                  onClick={() => setCurrentPage(currentPage + 1)}
                />
              ) : (
                <div />
              )}
            </div>
            <div className="mx-auto w-fit mt-3 font-semibold text-slate-700">
              <span>
                {currentPage * maxListings - maxListings + 1} to{" "}
                {Math.min(currentPage * maxListings, listings?.length)} of{" "}
                {listings?.length} properties
              </span>
            </div>
          </>
        ) : null}
      </div>
    </>
  );
}
